import React from 'react';
import { Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Panel, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import { fetchMcCases, watchMcCases, type McCaseCard } from '../../../services/missedCallService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import CaseStoryModal from './CaseStory';
import { statusChip } from './status';

type Scope = 'live' | 'done';

/**
 * Missed calls still waiting on the client: we have asked when to call back
 * and they have not picked a time yet. A call leaves this list the moment it
 * is finished, for Calls. Updates as things happen: the server says when.
 */
export function MissedCallsPage() {
  return <CasesPage scope="live" />;
}

/**
 * Every finished call, most recent first: a callback booked on an agent's
 * calendar, handed to a person, asked us to stop, or taken by the team.
 */
export function CallsPage() {
  return <CasesPage scope="done" />;
}

const FILTERS: Record<Scope, { value: string; label: string; test: (c: McCaseCard) => boolean }[]> = {
  live: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'waiting', label: 'No reply yet', test: (c) => c.status === 'message_sent' || c.status === 'replied' },
    { value: 'approval', label: 'Waiting for approval', test: (c) => c.status === 'message_held' },
  ],
  done: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'booked', label: 'Callback booked', test: (c) => c.status === 'callback_booked' },
    { value: 'person', label: 'Needs a person', test: (c) => c.status === 'needs_person' },
    { value: 'stopped', label: 'Asked us to stop', test: (c) => c.status === 'stopped' },
    { value: 'answered', label: 'Taken by the team', test: (c) => c.status === 'answered' },
  ],
};

/** What became of a call, in words, for the Call back column. */
function outcomeOf(c: McCaseCard): string {
  switch (c.status) {
    case 'callback_booked': return c.window ?? 'Callback booked';
    case 'needs_person': return c.needs_person_reason ?? 'Needs a person';
    case 'stopped': return 'No more messages to this number';
    case 'answered': return 'Someone on the team took the call';
    case 'message_held': return 'Our message is waiting for someone to approve it';
    default: return 'Waiting for them to pick a time';
  }
}

function CasesPage({ scope }: { scope: Scope }) {
  const live = scope === 'live';
  const fetcher = React.useCallback(() => fetchMcCases(scope), [scope]);
  const { data, error, loading, streaming, reconnecting } = useLive(fetcher, live ? 5000 : 15_000, { watch: watchMcCases });
  const [open, setOpen] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState('all');
  const all = data?.cases ?? [];
  const filters = FILTERS[scope];
  const chosen = filters.find((f) => f.value === show) ?? filters[0];
  const rows = all.filter(chosen.test).filter((c) => matches(query, [
    c.caller, c.caller_name, c.window, c.agent_name, c.language, c.needs_person_reason, statusChip(c.status).label,
  ]));

  const columns: ListColumn<McCaseCard>[] = [
    {
      key: 'caller',
      sortValue: (c) => c.caller_name || c.caller,
      header: 'Caller',
      grow: 1,
      minWidth: 160,
      render: (c) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {c.caller_name || c.caller}
          </Typography>
          {c.caller_name ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{c.caller}</Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'called',
      sortValue: (c) => c.called_at, sortStartsDescending: true,
      header: 'Called',
      width: 140,
      render: (c) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(c.called_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fromNow(c.called_at)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'status',
      sortValue: (c) => statusChip(c.status).label,
      header: live ? 'Now' : 'Result',
      width: 190,
      render: (c) => (
        <Stack spacing={0.25} alignItems="flex-start">
          <StatusChip {...statusChip(c.status)} />
          {live && c.last_message_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              Last message {fromNow(c.last_message_at)}
            </Typography>
          ) : null}
          {!live && c.completed_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{when(c.completed_at)}</Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'callback',
      sortValue: (c) => c.window_start || c.completed_at,
      header: live ? 'Where it stands' : 'Call back',
      grow: 1.6,
      minWidth: 220,
      render: (c) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{outcomeOf(c)}</Typography>
          {c.agent_name ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {`${c.agent_name} will call${c.language && c.language !== 'English' ? ` · in ${c.language}` : ''}`}
            </Typography>
          ) : c.language && c.language !== 'English' ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>Writes in {c.language}</Typography>
          ) : null}
        </Stack>
      ),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(c) => c.id}
      pageSize={25}
      loading={loading}
      onRowClick={(c) => setOpen(c.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : live
          ? 'No missed calls waiting. A call nobody picks up appears here within seconds.'
          : 'No finished calls yet. A call lands here once a callback is booked, a person takes it, or the client asks us to stop.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search calls…" />;
  const picker = (
    <SegmentedControl
      value={chosen.value}
      onChange={setShow}
      ariaLabel="Which calls"
      options={filters.map((f) => ({ value: f.value, label: `${f.label} · ${all.filter(f.test).length}` }))}
    />
  );
  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';
  const count = (status: McCaseCard['status']) => all.filter((c) => c.status === status).length;
  const subtitle = live
    ? `${all.length} waiting on the client · ${state}`
    : `${count('callback_booked')} callbacks booked · ${count('needs_person')} with a person · `
      + `${count('answered')} taken by the team · newest first`;

  return (
    <Stack spacing={2}>
      <Panel
        title={live ? 'Missed calls' : 'Calls'}
        subtitle={subtitle}
        search={searchBox}
        tools={picker}
        expanded={{ content: table, actions: <>{searchBox}{picker}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : table}
      </Panel>
      {open ? <CaseStoryModal caseId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
